import { useEffect, useMemo, useRef, useState } from "react";
import { Input, Modal as AntModal, message } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import { Button } from "../../../../shared";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../../../hooks/useCurrencySymbol";
import FeatureCard from "./FeatureCard";
import AddJackpotGameModal from "./AddJackpotGameModal";
import {
  DEFAULT_JACKPOT_CONFIG,
  formatJackpotAmount,
  isJackpotConfigEqual,
  isJackpotConfigValid,
  jackpotGameKey,
  parseJackpotAmount,
  type JackpotConfig,
  type JackpotGameRow,
} from "../jackpotConfig";
import {
  addJackpotGame,
  deleteJackpotGame,
  patchJackpotGameAmount,
  useJackpotGamesSearch,
} from "../../../../../queries/casino-configuration/features/useJackpotConfig";
import { useDebouncedSearchQuery } from "../../../../../hooks/useDebouncedSearchQuery";
import fallbackGameIcon from "../../../../../assets/icons/ic_casino.svg";

export interface JackpotCardProps {
  defaultExpanded?: boolean;
  initialConfig?: JackpotConfig;
  onSave?: (
    next: JackpotConfig,
    previous: JackpotConfig,
  ) => Promise<JackpotConfig | void> | JackpotConfig | void;
}

/**
 * Jackpot feature card — shared FeatureCard shell + rail body.
 * Add / amount edit / remove persist immediately (POST/PATCH/DELETE);
 * enable toggle still stages until Save.
 */
function JackpotCard({
  defaultExpanded = false,
  initialConfig = DEFAULT_JACKPOT_CONFIG,
  onSave,
}: JackpotCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [baseline, setBaseline] = useState<JackpotConfig>(initialConfig);
  const [config, setConfig] = useState<JackpotConfig>(initialConfig);
  const [saving, setSaving] = useState(false);
  const [patching, setPatching] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [railSearch, setRailSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editRaw, setEditRaw] = useState("");
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [pendingDisableConfirm, setPendingDisableConfirm] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  /** Target row waiting for discard confirmation when another row is mid-edit. */
  const [pendingEditSwitch, setPendingEditSwitch] =
    useState<JackpotGameRow | null>(null);
  const currencySymbol = useCurrencySymbol() ?? "$";

  const dirty = useMemo(
    () => !isJackpotConfigEqual(config, baseline),
    [config, baseline],
  );
  // Latest snapshots for the reseed effect (must not depend on dirty alone —
  // after a dirty refresh, baseline advances while config stays staged).
  const configRef = useRef(config);
  const baselineRef = useRef(baseline);
  configRef.current = config;
  baselineRef.current = baseline;

  // Re-seed from the query cache when it changes (after save/refresh). Always
  // advance baseline to the server snapshot; only replace staged config when
  // the operator has no unsaved edits — otherwise refresh would wipe them.
  useEffect(() => {
    const wasDirty = !isJackpotConfigEqual(
      configRef.current,
      baselineRef.current,
    );
    setBaseline(initialConfig);
    if (!wasDirty) {
      setConfig(initialConfig);
    }
  }, [initialConfig]);

  // Prompt on hard navigation / tab close while Jackpot has staged edits.
  // In-app SPA routes use BrowserRouter (no data router), so this is the
  // reliable unload guard for CASENG-926's unsaved-changes acceptance criterion.
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);
  const valid = useMemo(() => isJackpotConfigValid(config), [config]);
  const canSave = dirty && valid && !saving;

  const railKeys = useMemo(
    () => new Set(config.games.map((g) => jackpotGameKey(g))),
    [config.games],
  );

  const { effectiveSearchQuery } = useDebouncedSearchQuery(railSearch, {
    minLength: 1,
  });
  const {
    data: searchRows,
    isError: searchError,
    isFetching: searchFetching,
  } = useJackpotGamesSearch(effectiveSearchQuery);

  useEffect(() => {
    if (!searchError || !effectiveSearchQuery) return;
    message.error(AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_FETCH_ERROR);
  }, [searchError, effectiveSearchQuery]);

  const filteredGames = useMemo(() => {
    if (!effectiveSearchQuery) return config.games;
    const configByKey = new Map(
      config.games.map((g) => [jackpotGameKey(g), g] as const),
    );
    // Prefer local staged amount/remove state; drop rows removed from config.
    return (searchRows ?? [])
      .map((row) => configByKey.get(jackpotGameKey(row)) ?? null)
      .filter((row): row is JackpotGameRow => row !== null);
  }, [config.games, effectiveSearchQuery, searchRows]);

  const showSearchNoMatch =
    Boolean(effectiveSearchQuery) &&
    !searchFetching &&
    filteredGames.length === 0 &&
    config.games.length > 0;

  const showSearchLoading =
    Boolean(effectiveSearchQuery) &&
    searchFetching &&
    filteredGames.length === 0;

  const bodyDisabled = !config.enabled;

  const requestEnabledChange = (next: boolean) => {
    setConfig((prev) => ({ ...prev, enabled: next }));
  };

  const handleReset = () => {
    if (!dirty) return;
    setResetConfirmOpen(true);
  };

  const confirmReset = () => {
    setConfig(baseline);
    setEditingKey(null);
    setRemovingKey(null);
    setResetConfirmOpen(false);
  };

  const runSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const saved = await onSave?.(config, baseline);
      const persisted = saved ?? config;
      setBaseline({
        ...persisted,
        games: persisted.games.map((g) => ({ ...g, pending: false })),
      });
      setConfig({
        ...persisted,
        games: persisted.games.map((g) => ({ ...g, pending: false })),
      });
      message.success(AppStrings.CASINO_CONFIG_FEATURES_SAVE_SUCCESS);
    } catch (err) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    // Warn when turning the feature off — player-facing placements disappear.
    if (baseline.enabled && !config.enabled) {
      setPendingDisableConfirm(true);
      return;
    }
    await runSave();
  };

  const toastJackpotWriteError = (err: unknown) => {
    message.error(
      (err as Error)?.message ||
        AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE_ERROR,
    );
  };

  const handleAddGame = async (game: JackpotGameRow) => {
    try {
      await addJackpotGame({
        provider: game.provider,
        gameId: game.gameId,
        amount: game.amount,
      });
      // Keep baseline in sync so Save stays clean for an already-persisted add.
      setBaseline((prev) => ({
        ...prev,
        games: [...prev.games, game],
      }));
      setConfig((prev) => ({
        ...prev,
        games: [...prev.games, game],
      }));
      message.success(AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ADD_SUCCESS);
    } catch (err) {
      console.error("Failed to add jackpot game", err);
      toastJackpotWriteError(err);
      throw err;
    }
  };

  const applyEdit = (game: JackpotGameRow) => {
    setRemovingKey(null);
    setEditingKey(jackpotGameKey(game));
    setEditRaw(String(game.amount));
  };

  const startEdit = (game: JackpotGameRow) => {
    const key = jackpotGameKey(game);
    if (editingKey && editingKey !== key) {
      setPendingEditSwitch(game);
      return;
    }
    applyEdit(game);
  };

  const confirmEditSwitch = () => {
    if (pendingEditSwitch) applyEdit(pendingEditSwitch);
    setPendingEditSwitch(null);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditRaw("");
  };

  const commitEdit = async (game: JackpotGameRow) => {
    const amount = parseJackpotAmount(editRaw);
    if (amount === null || patching) return;
    if (amount === game.amount) {
      cancelEdit();
      return;
    }
    setPatching(true);
    try {
      await patchJackpotGameAmount({
        provider: game.provider,
        gameId: game.gameId,
        amount,
      });
      const applyAmount = (prev: JackpotConfig): JackpotConfig => ({
        ...prev,
        games: prev.games.map((g) =>
          jackpotGameKey(g) === jackpotGameKey(game) ? { ...g, amount } : g,
        ),
      });
      // Keep baseline in sync so Save stays clean for an already-persisted edit.
      setBaseline(applyAmount);
      setConfig(applyAmount);
      cancelEdit();
      message.success(AppStrings.CASINO_CONFIG_FEATURES_SAVE_SUCCESS);
    } catch (err) {
      console.error("Failed to patch jackpot game amount", err);
      toastJackpotWriteError(err);
    } finally {
      setPatching(false);
    }
  };

  const confirmRemove = async (game: JackpotGameRow) => {
    if (removing) return;
    setRemoving(true);
    try {
      await deleteJackpotGame({
        provider: game.provider,
        gameId: game.gameId,
      });
      const dropGame = (prev: JackpotConfig): JackpotConfig => ({
        ...prev,
        games: prev.games.filter(
          (g) => jackpotGameKey(g) !== jackpotGameKey(game),
        ),
      });
      // Keep baseline in sync so Save stays clean for an already-persisted remove.
      setBaseline(dropGame);
      setConfig(dropGame);
      setRemovingKey(null);
      if (editingKey === jackpotGameKey(game)) cancelEdit();
      message.success(AppStrings.CASINO_CONFIG_FEATURES_SAVE_SUCCESS);
    } catch (err) {
      console.error("Failed to delete jackpot game", err);
      toastJackpotWriteError(err);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <FeatureCard
        cardId="jackpot"
        name={AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_NAME}
        description={AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_DESCRIPTION}
        enabled={config.enabled}
        onEnabledChange={requestEnabledChange}
        enableDisabled={saving}
        expanded={expanded}
        onToggleExpand={() => setExpanded((prev) => !prev)}
      >
        <div
          className={`jackpot-body${bodyDisabled ? " jackpot-body--disabled" : ""}`}
          data-testid="jackpot-body"
          aria-disabled={bodyDisabled}
        >
          <div className="jackpot-body-header">
            <h3 className="jackpot-games-heading">
              {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_GAMES_HEADING}
            </h3>
            <p className="jackpot-explainer">
              {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_EXPLAINER}
            </p>
          </div>

          <div className="jackpot-rail" data-testid="jackpot-rail">
            <div className="jackpot-rail-toolbar">
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder={
                  AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SEARCH_RAIL
                }
                value={railSearch}
                onChange={(e) => setRailSearch(e.target.value)}
                disabled={bodyDisabled}
                className="jackpot-rail-search"
                data-testid="jackpot-rail-search"
              />
              <Button
                intent="secondary"
                onClick={() => setAddOpen(true)}
                disabled={bodyDisabled}
                data-testid="jackpot-add-game"
              >
                {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ADD_GAME}
              </Button>
            </div>

            {config.games.length === 0 ? (
              <div
                className="jackpot-rail-empty"
                data-testid="jackpot-rail-empty"
              >
                {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_EMPTY}
              </div>
            ) : showSearchLoading ? (
              <div
                className="jackpot-rail-empty"
                data-testid="jackpot-rail-searching"
              >
                {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SEARCHING}
              </div>
            ) : showSearchNoMatch ? (
              <div
                className="jackpot-rail-empty"
                data-testid="jackpot-rail-no-match"
              >
                {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_NO_MATCH}
              </div>
            ) : filteredGames.length === 0 ? (
              <div
                className="jackpot-rail-empty"
                data-testid="jackpot-rail-empty"
              >
                {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_EMPTY}
              </div>
            ) : (
                <ul className="jackpot-rail-list">
                  {filteredGames.map((game) => {
                    const key = jackpotGameKey(game);
                    const isEditing = editingKey === key;
                    const isRemoving = removingKey === key;
                    const editAmount = parseJackpotAmount(editRaw);
                    return (
                      <li
                        key={key}
                        className={`jackpot-rail-row${
                          game.pending ? " jackpot-rail-row--pending" : ""
                        }`}
                        data-testid={`jackpot-rail-row-${game.gameId}`}
                      >
                        <img
                          className="jackpot-rail-thumb"
                          src={game.image || fallbackGameIcon}
                          alt=""
                          data-testid={`jackpot-thumb-${game.gameId}`}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              fallbackGameIcon;
                          }}
                        />
                        <div className="jackpot-rail-meta">
                          <div className="jackpot-rail-name">{game.name}</div>
                          <div className="jackpot-rail-id">
                            {game.provider}/{game.gameId}
                          </div>
                          <span className="jackpot-status-pill jackpot-status-pill--active">
                            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_ACTIVE}
                          </span>
                        </div>

                        {isEditing ? (
                          <div className="jackpot-inline-edit">
                            <Input
                              prefix={currencySymbol}
                              type="number"
                              step={0.01}
                              min={0.01}
                              value={editRaw}
                              onChange={(e) => setEditRaw(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  void commitEdit(game);
                                }
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  if (!patching) cancelEdit();
                                }
                              }}
                              disabled={bodyDisabled || patching}
                              data-testid={`jackpot-edit-amount-${game.gameId}`}
                            />
                            <Button
                              intent="primary"
                              onClick={() => {
                                void commitEdit(game);
                              }}
                              disabled={
                                editAmount === null || bodyDisabled || patching
                              }
                              loading={patching}
                              data-testid={`jackpot-edit-save-${game.gameId}`}
                            >
                              {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_EDIT_SAVE}
                            </Button>
                            <button
                              type="button"
                              className="jackpot-text-btn"
                              onClick={cancelEdit}
                              disabled={patching}
                              data-testid={`jackpot-edit-cancel-${game.gameId}`}
                            >
                              {AppStrings.CASINO_CONFIG_FEATURES_CONFIRM_CANCEL}
                            </button>
                            {editRaw !== "" && editAmount === null ? (
                              <span className="jackpot-inline-error">
                                {
                                  AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_AMOUNT_ERROR
                                }
                              </span>
                            ) : null}
                          </div>
                        ) : (
                          <span
                            className="jackpot-rail-amount"
                            data-testid={`jackpot-amount-${game.gameId}`}
                          >
                            {formatJackpotAmount(game.amount, currencySymbol)}
                          </span>
                        )}

                        {isRemoving ? (
                          <div className="jackpot-inline-remove">
                            <span>
                              {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_REMOVE}
                            </span>
                            <Button
                              intent="primary"
                              className="jackpot-danger-btn"
                              onClick={() => {
                                void confirmRemove(game);
                              }}
                              disabled={bodyDisabled || removing}
                              loading={removing}
                              data-testid={`jackpot-remove-confirm-${game.gameId}`}
                            >
                              {
                                AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_REMOVE_CONFIRM
                              }
                            </Button>
                            <button
                              type="button"
                              className="jackpot-text-btn"
                              onClick={() => setRemovingKey(null)}
                              disabled={removing}
                              data-testid={`jackpot-remove-cancel-${game.gameId}`}
                            >
                              {AppStrings.CASINO_CONFIG_FEATURES_CONFIRM_CANCEL}
                            </button>
                          </div>
                        ) : (
                          <div className="jackpot-rail-actions">
                            <button
                              type="button"
                              className="jackpot-icon-btn"
                              aria-label={
                                AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_EDIT_AMOUNT
                              }
                              disabled={bodyDisabled}
                              onClick={() => startEdit(game)}
                              data-testid={`jackpot-edit-${game.gameId}`}
                            >
                              <EditOutlined />
                            </button>
                            <button
                              type="button"
                              className="jackpot-icon-btn"
                              aria-label={
                                AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_REMOVE
                              }
                              disabled={bodyDisabled}
                              onClick={() => {
                                setEditingKey(null);
                                setRemovingKey(key);
                              }}
                              data-testid={`jackpot-remove-${game.gameId}`}
                            >
                              <DeleteOutlined />
                            </button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
          </div>
        </div>

        <div className="feature-card-actions">
          <Button
            intent="secondary"
            onClick={handleReset}
            disabled={!dirty || saving}
            data-testid="jackpot-reset"
          >
            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_RESET}
          </Button>
          <Button
            intent="primary"
            onClick={handleSave}
            disabled={!canSave}
            loading={saving}
            data-testid="jackpot-save"
          >
            {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_SAVE}
          </Button>
        </div>
      </FeatureCard>

      <AddJackpotGameModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        railKeys={railKeys}
        onConfirm={handleAddGame}
        currencySymbol={currencySymbol}
      />

      <AntModal
        open={pendingDisableConfirm}
        onCancel={() => setPendingDisableConfirm(false)}
        title={
          AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_DISABLE_CONFIRM_TITLE
        }
        okText={
          AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_DISABLE_CONFIRM_BTN
        }
        cancelText={AppStrings.CASINO_CONFIG_FEATURES_CONFIRM_CANCEL}
        onOk={async () => {
          setPendingDisableConfirm(false);
          await runSave();
        }}
        data-testid="jackpot-disable-confirm"
      >
        <p className="feature-confirm-text">
          {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_DISABLE_CONFIRM_DESC}
        </p>
      </AntModal>

      <AntModal
        open={resetConfirmOpen}
        onCancel={() => setResetConfirmOpen(false)}
        title={AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_RESET_CONFIRM_TITLE}
        okText={AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_RESET}
        cancelText={AppStrings.CASINO_CONFIG_FEATURES_CONFIRM_CANCEL}
        onOk={confirmReset}
        data-testid="jackpot-reset-confirm"
      >
        <p className="feature-confirm-text">
          {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_RESET_CONFIRM_DESC}
        </p>
      </AntModal>

      <AntModal
        open={pendingEditSwitch !== null}
        onCancel={() => setPendingEditSwitch(null)}
        title={AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_UNSAVED_EDIT_TITLE}
        okText={AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_UNSAVED_EDIT_CONFIRM}
        cancelText={AppStrings.CASINO_CONFIG_FEATURES_CONFIRM_CANCEL}
        onOk={confirmEditSwitch}
        data-testid="jackpot-unsaved-edit-confirm"
      >
        <p className="feature-confirm-text">
          {AppStrings.CASINO_CONFIG_FEATURES_JACKPOT_UNSAVED_EDIT}
        </p>
      </AntModal>
    </>
  );
}

export default JackpotCard;
